import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import BlockRenderer from "@/components/memora/BlockRenderer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, Plus, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function makeSessionId() {
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function Memora() {
  const [sessionId, setSessionId] = useState(makeSessionId);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadSuggestions = async () => {
    try {
      const { data } = await api.get("/chat/suggestions");
      setSuggestions(data?.suggestions || []);
    } catch { /* ignore */ }
  };
  const loadSessions = async () => {
    try {
      const { data } = await api.get("/chat/sessions/list");
      setSessions(data?.sessions || []);
    } catch { /* ignore */ }
  };
  const loadSession = async (id) => {
    setSessionId(id);
    try {
      const { data } = await api.get(`/chat/${id}`);
      setMessages(data?.messages || []);
    } catch { setMessages([]); }
  };

  useEffect(() => { loadSuggestions(); loadSessions(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput("");
    const userMsg = { role: "user", content: message, msg_id: `local_${Date.now()}` };
    setMessages((m) => [...m, userMsg]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message, provider: "claude" });
      const assistantMsg = {
        role: "assistant",
        content: data.answer,
        blocks: data.blocks || [],
        citations: data.citations || [],
        msg_id: data.message_id,
      };
      setMessages((m) => [...m, assistantMsg]);
      loadSessions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Memora is unavailable right now");
    } finally {
      setSending(false);
    }
  };

  const startNew = () => {
    setSessionId(makeSessionId());
    setMessages([]);
    loadSuggestions();
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/chat/sessions/${id}`);
      if (id === sessionId) startNew();
      loadSessions();
    } catch { /* ignore */ }
  };

  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-6 -mx-5 md:-mx-10 -my-6 md:-my-10 min-h-[calc(100vh-6rem)]">
      {/* Sessions */}
      <aside className="hidden md:block bg-white border-r border-neutral-200 p-3 space-y-2">
        <Button data-testid="memora-new-chat" onClick={startNew} className="w-full" variant="outline">
          <Plus className="w-4 h-4 mr-2" /> New chat
        </Button>
        <div className="pt-2 space-y-1 max-h-[calc(100vh-10rem)] overflow-y-auto">
          {sessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => loadSession(s.session_id)}
              data-testid={`session-${s.session_id}`}
              className={cn(
                "w-full text-left px-2 py-2 rounded-lg text-sm flex items-start gap-2 group",
                s.session_id === sessionId ? "bg-neutral-100" : "hover:bg-neutral-50"
              )}
            >
              <MessageSquare className="w-3.5 h-3.5 text-neutral-400 mt-1 shrink-0" />
              <span className="flex-1 truncate text-neutral-800">{s.title || "New conversation"}</span>
              <span onClick={(e) => deleteSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 p-1">
                <Trash2 className="w-3.5 h-3.5 text-neutral-400 hover:text-rose-600" />
              </span>
            </button>
          ))}
        </div>
      </aside>

      {/* Chat */}
      <div className="flex flex-col">
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 md:px-10 py-6 md:py-10">
          {messages.length === 0 && (
            <div className="max-w-2xl">
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Ask Memora anything</h1>
              <p className="text-neutral-600 mt-2">I remember your labs, visits and everything you&apos;ve told me.</p>
              {suggestions.length > 0 && (
                <div className="mt-6 space-y-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={s.id || i}
                      onClick={() => send(s.prompt || s.label || s)}
                      data-testid={`suggestion-${i}`}
                      className="w-full text-left px-4 py-3 bg-white border border-neutral-200 rounded-xl hover:border-neutral-300 text-neutral-800"
                    >
                      {s.label || s.prompt || s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="max-w-3xl space-y-8">
            {messages.map((m, idx) => {
              if (m.role === "user") {
                return (
                  <div key={m.msg_id || idx} className="flex justify-end">
                    <div className="max-w-[85%] bg-neutral-900 text-white rounded-2xl px-4 py-2.5">
                      <p className="whitespace-pre-wrap text-sm">{m.content}</p>
                    </div>
                  </div>
                );
              }
              const blocks = m.blocks && m.blocks.length ? m.blocks : (m.content ? [{ kind: "text", text: m.content }] : []);
              return (
                <div key={m.msg_id || idx} data-testid={`memora-msg-${idx}`}>
                  <BlockRenderer blocks={blocks} />
                </div>
              );
            })}
            {sending && (
              <div className="flex items-center gap-2 text-neutral-500 text-sm">
                <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-pulse" />
                Memora is thinking…
              </div>
            )}
          </div>
        </div>

        {/* Composer */}
        <div className="border-t border-neutral-200 bg-white px-5 md:px-10 py-4">
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="max-w-3xl flex items-center gap-2"
          >
            <Input
              data-testid="memora-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Memora…"
              disabled={sending}
            />
            <Button data-testid="memora-send" type="submit" disabled={sending || !input.trim()}>
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
